import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { documentsApi, pdvApi, documentsMatrixApi } from '../lib/api';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Calendar,
  Building,
  Pencil,
  Trash2,
  AlertTriangle,
  Download,
  Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const CATEGORIES = [
  { value: 'planimetria', label: 'Planimetria' },
  { value: 'relazione_tecnica', label: 'Relazione Tecnica' },
  { value: 'certificato', label: 'Certificato' },
  { value: 'cpi', label: 'CPI (Certificato Prevenzione Incendi)' },
  { value: 'agibilita', label: 'Agibilità' },
  { value: 'dichiarazione_conformita', label: 'Dichiarazione Conformità' },
  { value: 'altro', label: 'Altro' }
];

const StatusBadge = ({ status }) => {
  const variants = {
    valid: 'bg-emerald-100 text-emerald-800',
    expiring: 'bg-amber-100 text-amber-800',
    expired: 'bg-rose-100 text-rose-800'
  };

  const labels = {
    valid: 'Valido',
    expiring: 'In Scadenza',
    expired: 'Scaduto'
  };

  return (
    <Badge className={variants[status] || variants.valid}>
      {labels[status] || status}
    </Badge>
  );
};

const DocumentForm = ({ document, pdvs, onSubmit, onCancel, defaultPdvId, requiredTypes = [] }) => {
  const [formData, setFormData] = useState(document || {
    pdv_id: defaultPdvId || '',
    name: '',
    category: '',
    description: '',
    file_url: '',
    expiry_date: '',
    reminder_days: 30
  });
  const [uploading, setUploading] = useState(false);

  // Auto-fill quando si seleziona una tipologia: nome documento + (se ha scadenza) data + 30gg reminder
  const handleCategoryChange = (value) => {
    const tpl = requiredTypes.find(t => t.code === value);
    setFormData(prev => ({
      ...prev,
      category: value,
      name: prev.name || (tpl ? tpl.name : ''),
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/upload/documents`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      const data = await response.json();
      setFormData({ ...formData, file_url: data.url, name: formData.name || file.name.replace(/\.[^/.]+$/, '') });
      toast.success('File caricato con successo');
    } catch (error) {
      toast.error('Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pdv_id">Unità Locale *</Label>
        <Select
          value={formData.pdv_id}
          onValueChange={(value) => setFormData({ ...formData, pdv_id: value })}
        >
          <SelectTrigger data-testid="doc-pdv-select">
            <SelectValue placeholder="Seleziona UL" />
          </SelectTrigger>
          <SelectContent>
            {pdvs.map((pdv) => (
              <SelectItem key={pdv.id} value={pdv.id}>
                {pdv.name} - {pdv.city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Nome Documento *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Certificato antincendio 2024"
          required
          data-testid="doc-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Tipologia documento * <span className="text-xs text-muted-foreground">(determina la riga nella Matrice Compliance)</span></Label>
        <Select
          value={formData.category}
          onValueChange={handleCategoryChange}
        >
          <SelectTrigger data-testid="doc-category-select">
            <SelectValue placeholder="Seleziona tipologia..." />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {requiredTypes.length === 0
              ? CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))
              : requiredTypes.map(t => (
                  <SelectItem key={t.code} value={t.code}>
                    {t.name}{t.has_expiry ? ' (con scadenza)' : ''}
                  </SelectItem>
                ))}
          </SelectContent>
        </Select>
        {formData.category && requiredTypes.find(t => t.code === formData.category && !t.has_expiry) && (
          <p className="text-xs text-emerald-700">
            ℹ️ Questa tipologia <strong>non richiede scadenza</strong>: appena salvi, il pallino diventa verde nella matrice.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descrizione</Label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Note aggiuntive..."
          className="w-full min-h-[60px] px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
          data-testid="doc-description-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="expiry_date">Data Scadenza</Label>
          <Input
            id="expiry_date"
            type="date"
            value={formData.expiry_date}
            onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
            data-testid="doc-expiry-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reminder_days">Giorni Promemoria</Label>
          <Input
            id="reminder_days"
            type="number"
            value={formData.reminder_days}
            onChange={(e) => setFormData({ ...formData, reminder_days: parseInt(e.target.value) || 30 })}
            min="1"
            data-testid="doc-reminder-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Carica File</Label>
        <div className="flex items-center gap-2">
          <Input
            type="file"
            onChange={handleFileUpload}
            disabled={uploading}
            className="flex-1"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            data-testid="doc-file-input"
          />
          {uploading && <span className="text-sm text-muted-foreground">Caricamento...</span>}
        </div>
        {formData.file_url && (
          <p className="text-xs text-emerald-600 flex items-center gap-1">
            <Upload className="w-3 h-3" /> File caricato
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="file_url">Oppure inserisci URL</Label>
        <Input
          id="file_url"
          value={formData.file_url}
          onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
          placeholder="https://..."
          data-testid="doc-url-input"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annulla
        </Button>
        <Button type="submit" data-testid="doc-submit-btn">
          {document ? 'Salva Modifiche' : 'Aggiungi Documento'}
        </Button>
      </div>
    </form>
  );
};

export default function DocumentsPage() {
  const { currentPdvId } = useCurrentPdv();
  const { user } = useAuth();
  const isAzienda = user?.role === 'azienda';
  const [documents, setDocuments] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPdv, setFilterPdv] = useState(currentPdvId || 'all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [requiredTypes, setRequiredTypes] = useState([]);

  // Auto-sync with global UL selection
  useEffect(() => {
    if (currentPdvId && currentPdvId !== filterPdv) {
      setFilterPdv(currentPdvId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPdvId]);

  const fetchData = async () => {
    try {
      const [docsRes, pdvsRes, typesRes] = await Promise.all([
        documentsApi.getAll(),
        pdvApi.getAll(),
        documentsMatrixApi.listTypes().catch(() => ({ data: [] })),
      ]);
      setRequiredTypes(typesRes.data || []);
      setDocuments(docsRes.data);
      setPdvs(pdvsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (data) => {
    try {
      if (editingDoc) {
        await documentsApi.update(editingDoc.id, data);
        toast.success('Documento aggiornato');
      } else {
        await documentsApi.create(data);
        toast.success('Documento aggiunto');
      }
      setDialogOpen(false);
      setEditingDoc(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setDialogOpen(true);
  };

  const handleDeleteClick = (doc) => {
    if (isAzienda) {
      toast.error(
        "Per cancellare questo file devi essere autorizzato dall'amministratore. Contattalo per procedere.",
        { duration: 6000 }
      );
      return;
    }
    setDocToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!docToDelete) return;
    try {
      await documentsApi.delete(docToDelete.id);
      toast.success('Documento eliminato');
      setDeleteDialogOpen(false);
      setDocToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const getPdvName = (pdvId) => {
    const pdv = pdvs.find(p => p.id === pdvId);
    return pdv ? pdv.name : 'N/D';
  };

  const getCategoryLabel = (cat) => {
    const category = CATEGORIES.find(c => c.value === cat);
    return category ? category.label : cat;
  };

  const filteredDocs = documents.filter((doc) => {
    const matchesSearch = doc.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = filterCategory === 'all' || doc.category === filterCategory;
    const matchesPdv = filterPdv === 'all' || doc.pdv_id === filterPdv;
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    return matchesSearch && matchesCategory && matchesPdv && matchesStatus;
  });

  const expiredCount = documents.filter(d => d.status === 'expired').length;
  const expiringCount = documents.filter(d => d.status === 'expiring').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="page-header mb-0">
          <h1 className="page-title">Documenti</h1>
          <p className="page-subtitle">{documents.length} documenti totali</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingDoc(null);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-doc-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Documento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingDoc ? 'Modifica Documento' : 'Nuovo Documento'}
              </DialogTitle>
            </DialogHeader>
            <DocumentForm
              document={editingDoc}
              pdvs={pdvs}
              defaultPdvId={currentPdvId}
              requiredTypes={requiredTypes}
              onSubmit={handleSubmit}
              onCancel={() => {
                setDialogOpen(false);
                setEditingDoc(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Alert Banner */}
      {(expiredCount > 0 || expiringCount > 0) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="alert-banner warning mb-6 flex items-center gap-3"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">
            <strong>{expiredCount} documenti scaduti</strong> e <strong>{expiringCount} in scadenza</strong> nei prossimi 30 giorni
          </p>
        </motion.div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cerca documenti..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="doc-search-input"
          />
        </div>
        <Select value={filterPdv} onValueChange={setFilterPdv}>
          <SelectTrigger data-testid="filter-pdv">
            <Building className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Tutte le UL" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le UL</SelectItem>
            {pdvs.map((pdv) => (
              <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger data-testid="filter-category">
            <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger data-testid="filter-status">
            <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="valid">Validi</SelectItem>
            <SelectItem value="expiring">In Scadenza</SelectItem>
            <SelectItem value="expired">Scaduti</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {filteredDocs.length > 0 ? (
        <div className="border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="table-header">Documento</TableHead>
                <TableHead className="table-header">UL</TableHead>
                <TableHead className="table-header">Categoria</TableHead>
                <TableHead className="table-header">Scadenza</TableHead>
                <TableHead className="table-header">Stato</TableHead>
                <TableHead className="table-header w-[100px]">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocs.map((doc) => (
                <TableRow key={doc.id} className="data-row" data-testid={`doc-row-${doc.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        {doc.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">{doc.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getPdvName(doc.pdv_id)}</TableCell>
                  <TableCell>{getCategoryLabel(doc.category)}</TableCell>
                  <TableCell>
                    {doc.expiry_date 
                      ? new Date(doc.expiry_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={doc.status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {doc.file_url && (
                        <Button variant="ghost" size="icon" asChild className="h-8 w-8">
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handleEdit(doc)}
                        data-testid={`edit-doc-${doc.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteClick(doc)}
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="empty-state">
          <FileText className="empty-state-icon" />
          <p className="empty-state-title">
            {search || filterCategory !== 'all' || filterPdv !== 'all' || filterStatus !== 'all'
              ? 'Nessun risultato'
              : 'Nessun documento'
            }
          </p>
          <p className="empty-state-description">
            {search || filterCategory !== 'all' || filterPdv !== 'all' || filterStatus !== 'all'
              ? 'Prova a modificare i filtri'
              : 'Inizia aggiungendo il primo documento'
            }
          </p>
          {!search && filterCategory === 'all' && filterPdv === 'all' && filterStatus === 'all' && (
            <Button className="mt-4" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi documento
            </Button>
          )}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare "{docToDelete?.name}"? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-doc"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
